import {
    Accessibility,
    CalendarCheck,
    CreditCard,
    ExternalLink,
    Mail,
    MapPin,
    Navigation,
    PawPrint,
    Phone,
} from "lucide-react";
import business from "../data/business.json";
import { SectionTitle } from "./SectionTitle";
import { Reveal } from "./Reveal";
import { currentRomeDayIndex } from "../lib/hours";

const SERVICE_ICONS = {
    card: CreditCard,
    accessibility: Accessibility,
    paw: PawPrint,
    calendar: CalendarCheck,
};

const MAP_EMBED = "https://www.google.com/maps?q=43.83889,10.9676828&z=16&output=embed";

export const LocationSection = ({ consent, onLoadMap }) => {
    const todayIdx = currentRomeDayIndex();
    return (
        <section id="dove-siamo" className="bg-superficie py-20 md:py-28">
            <div className="container-pub">
                <Reveal>
                    <SectionTitle eyebrow="Dove siamo" title={`Via Covona 160, ${business.address.city}`} />
                </Reveal>
                <div className="mt-10 grid lg:grid-cols-2 gap-10 items-start">
                    <Reveal>
                        <ul className="space-y-4">
                            <li className="flex items-start gap-3">
                                <MapPin className="w-5 h-5 text-ambra shrink-0 mt-1" />
                                <div>
                                    <p>
                                        {business.address.street}, {business.address.zip} {business.address.city} (
                                        {business.address.province})
                                    </p>
                                    <p className="text-sm text-avorio/60">Plus code: {business.plusCode}</p>
                                </div>
                            </li>
                            <li className="flex items-center gap-3">
                                <Phone className="w-5 h-5 text-ambra shrink-0" />
                                <a
                                    className="tabular hover:text-ambra transition-colors"
                                    href={business.phone.href}
                                    data-testid="location-phone-link"
                                >
                                    {business.phone.display}
                                </a>
                            </li>
                            <li className="flex items-center gap-3">
                                <Mail className="w-5 h-5 text-ambra shrink-0" />
                                <a className="hover:text-ambra" href={`mailto:${business.pec}`} data-testid="location-pec-link">
                                    PEC: {business.pec}
                                </a>
                            </li>
                        </ul>
                        <h3 className="font-heading uppercase tracking-wide text-xl mt-8 mb-3">Orari</h3>
                        <table className="w-full max-w-md text-[15px] tabular" data-testid="hours-table">
                            <tbody>
                                {business.weeklyHours.map((row, i) => {
                                    const isToday = i === todayIdx;
                                    return (
                                        <tr
                                            key={row.day}
                                            className={`${isToday ? "bg-ambra/10 font-semibold" : ""} border-b border-bordo/60 last:border-0`}
                                            data-testid={isToday ? "hours-row-today" : undefined}
                                        >
                                            <th
                                                scope="row"
                                                className={`text-left py-2.5 pr-4 font-normal ${
                                                    isToday ? "border-l-2 border-ambra pl-2.5" : ""
                                                }`}
                                            >
                                                {row.day}
                                                {isToday && <span className="sr-only"> (oggi)</span>}
                                            </th>
                                            <td className={`py-2.5 text-right ${row.open ? "" : "text-avorio/60"}`}>
                                                {row.open ? `${row.open} – ${row.close}` : "Chiuso"}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <p className="mt-6 text-avorio/85 max-w-md">
                            Siamo appena fuori dal centro di Quarrata, in una zona tranquilla, con parcheggio
                            gratuito anche in strada.
                        </p>
                        <ul className="mt-6 flex flex-wrap gap-2" data-testid="services-list">
                            {business.services.map((s) => {
                                const Icon = SERVICE_ICONS[s.icon];
                                return (
                                    <li key={s.label} className="chip !min-h-0 !py-2 text-[13px]">
                                        {Icon && <Icon className="w-4 h-4 text-ambra" />} {s.label}
                                    </li>
                                );
                            })}
                        </ul>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div
                            className="rounded-xl overflow-hidden border border-bordo aspect-[4/3] relative bg-carbone"
                            data-testid="map-card"
                        >
                            {consent === "accepted" ? (
                                <iframe
                                    title="Mappa: River Pub, Via Covona 160, Quarrata (PT)"
                                    src={MAP_EMBED}
                                    className="w-full h-full border-0"
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    data-testid="map-iframe"
                                />
                            ) : (
                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center p-6">
                                    <MapPin className="w-8 h-8 text-ambra" strokeWidth={1.25} />
                                    <p className="text-sm text-avorio/70 max-w-xs">
                                        La mappa è un servizio di Google: si carica solo dopo il tuo consenso.
                                    </p>
                                    <button className="btn btn-amber" onClick={onLoadMap} data-testid="map-load-btn">
                                        Carica la mappa
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="mt-4 flex flex-wrap gap-3">
                            <a
                                className="btn btn-amber"
                                href={business.links.mapsDirections}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="maps-directions-btn"
                            >
                                <Navigation className="w-[18px] h-[18px]" /> Indicazioni
                            </a>
                            <a
                                className="btn btn-outline"
                                href={business.links.mapsPlace}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="maps-open-btn"
                            >
                                Apri in Google Maps <ExternalLink className="w-4 h-4" />
                            </a>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};